/**
 * Country values used by Contentful location tags are deliberately kept as
 * slugs. Keep this normalization in one place so client-side locale prefills
 * and audience matching cannot drift apart.
 */
export const countrySlugAliasByIsoCode: Record<string, string[]> = {
  GB: ['country-united-kingdom'],
  UK: ['country-united-kingdom'],
  US: ['country-united-states'],
}

/** Convert a country display name to the location-tag slug convention. */
export const toCountrySlug = (value: string): string => {
  const normalizedValue = value.trim()
  if (!normalizedValue) return ''

  const slug = normalizedValue
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')

  return slug ? `country-${slug}` : ''
}

const getDisplayName = (countryCode: string): string => {
  try {
    return (
      new Intl.DisplayNames(['en'], { type: 'region' }).of(countryCode) || ''
    )
  } catch {
    return ''
  }
}

/**
 * Return location-tag candidates for a Contentful tag, ISO-2 code, or country
 * name. Candidate ordering preserves the established alias-first behavior.
 */
export const getCountrySlugCandidates = (value: string): string[] => {
  const trimmedValue = value.trim()
  if (!trimmedValue) return []

  if (trimmedValue.toLowerCase().startsWith('country-')) {
    return [trimmedValue]
  }

  const normalizedCode = trimmedValue.toUpperCase()
  const aliases = countrySlugAliasByIsoCode[normalizedCode] || []
  const displayName =
    trimmedValue.length === 2 ? getDisplayName(normalizedCode) : ''
  if (
    trimmedValue.length === 2 &&
    (!displayName || displayName.toLowerCase() === 'unknown region')
  ) {
    return aliases
  }
  const inferredSlug = toCountrySlug(displayName || trimmedValue)

  return Array.from(new Set([...aliases, inferredSlug].filter(Boolean)))
}

/** Resolve a geo ISO code only when its candidate exists in the options. */
export const resolveCountryValue = (
  countryCode: string,
  countryOptions: Array<{ value?: string | null }>,
): string => {
  const candidate = getCountrySlugCandidates(countryCode).find((slug) =>
    countryOptions.some((option) => option.value === slug),
  )

  return candidate || ''
}
